import { Alert, Box, Stack } from "@mui/material";
import { isNil } from "lodash-es";
import { useState } from "react";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import ViewContentBlockHeader from "@/App/Layouts/ViewContentBlockHeader";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import AuthorizedElement from "@/common/components/Auth/AuthorizedElement";
import AppIconButton from "@/common/components/Button/AppIconButton";
import DataTabular from "@/common/components/DataTabular/DataTabular";
import Datetime from "@/common/components/Datetime/Datetime";
import GeneralByWhoDisplay from "@/common/components/Entity/General/Display/GeneralByWhoDisplay";
import GeneralDiscountDisplay from "@/common/components/Entity/General/GeneralDiscount/GeneralDiscountDisplay";
import GeneralTaxDisplay from "@/common/components/Entity/General/GeneralTax/GeneralTaxDisplay";
import VehicleDamageLink from "@/common/components/Entity/VehicleDamage/VehicleDamageLink";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import InlineApiEnumValue from "@/common/components/Enum/InlineApiEnumValue";
import FileListView from "@/common/components/Files/FileListView";
import CurrencyValue from "@/common/components/Form/Display/CurrencyValue";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import GeneralStageHistory from "@/common/components/GeneralStageHistory";
import AppIcon from "@/common/components/Icons/AppIcon";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditMileageModal from "@/common/components/Modals/common/BaseEditMileageModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import GeneralPriceSummaryDisplay from "@/common/components/PriceSummary/GeneralPriceSummaryDisplay";
import AppTypography from "@/common/components/Text/AppTypography";
import AppTooltip from "@/common/components/Tooltip/AppTooltip";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { useAuthorizationInfo } from "@/common/hooks/auth/useAuthorizationInfo";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import {
  AppPermission,
  EntityType,
  RepairOperationDto,
  RepairOperationStage,
} from "@/core/api/generated/v1";

import ContractLink from "../../../Contract/ContractLink";
import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import SupplierLink from "../../../Supplier/SupplierLink";
import VehicleLink from "../../../Vehicle/VehicleLink";

interface Props {
  repairOperation: RepairOperationDto;
  handleUpdateStage: (newStage: RepairOperationStage) => Promise<void>;
  onUpdate: (newValue?: RepairOperationDto) => void;
}

export default function OverviewTabContent({
  repairOperation,
  handleUpdateStage,
  onUpdate,
}: Props) {
  const { hasPermissions } = useAuthorizationInfo();

  const [isUpdateMileageModalOpened, setIsUpdateMileageModalOpened] = useState(false);

  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={repairOperation} />}
          />
        }
      >
        <ViewContentBlock>
          {repairOperation?.stageHistory?.stage === RepairOperationStage.Draft && (
            <AuthorizedElement permissions={[AppPermission.RepairOperationManage]}>
              <Alert severity='info' icon={<AppIcon of='tip' />}>
                This operation is in{" "}
                <strong>
                  <InlineApiEnumValue
                    type='RepairOperationStage'
                    value={RepairOperationStage.Draft}
                  />
                </strong>{" "}
                stage. Please review and complete it in order for changes to take effect.
              </Alert>
            </AuthorizedElement>
          )}

          {/* <VehicleBlock
            sx={{ maxWidth: { md: "400px" } }}
            variant='reference'
            vehicle={repairOperation.vehicle}
            withDetailsToggle
            withLink
          /> */}

          <Stack spacing={1}>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              {/* <InfoTile
              label='Number'
              iconOf='localNumber'
              isEmpty={isNil(repairOperation?.localNumber)}
            >
              {repairOperation?.localNumber}
            </InfoTile> */}

              <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={isNil(repairOperation?.vehicle)}>
                <VehicleLink entity={repairOperation?.vehicle} />
              </InfoTile>

              <InfoTile
                label='Contract'
                iconOf='contract'
                isEmpty={isNil(repairOperation?.contract)}
              >
                <ContractLink entity={repairOperation?.contract} />
              </InfoTile>

              <InfoTile
                label='Supplier'
                iconOf='supplier'
                isEmpty={isNil(repairOperation?.supplier)}
              >
                <SupplierLink entity={repairOperation?.supplier} />
              </InfoTile>

              <InfoTile
                label='External number'
                iconOf='externalNumber'
                isEmpty={!repairOperation.externalNumber}
                endAdornment={
                  <BaseEditExternalNumberModal
                    initialValues={{ externalNumber: repairOperation.externalNumber }}
                    onConfirm={async (externalNumber) => {
                      await apiClient.repairOperationsApi.apiV1RepairOperationsRepairOperationIdExternalNumberPut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          repairOperationId: repairOperation.id || "",
                          generalExternalNumberInputDto: { externalNumber },
                        },
                      );
                      onUpdate();
                    }}
                  />
                }
              >
                {repairOperation.externalNumber}
              </InfoTile>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <InfoTile label='Name' iconOf='data' isEmpty={isNil(repairOperation?.name)}>
                {repairOperation?.name}
              </InfoTile>

              <InfoTile
                label='Description'
                iconOf='data'
                isEmpty={isNil(repairOperation?.description)}
              >
                {repairOperation?.name}
              </InfoTile>

              <InfoTile
                label='Notes'
                iconOf='notes'
                isEmpty={!repairOperation.notes}
                endAdornment={
                  <BaseEditNotesModal
                    initialValues={{ notes: repairOperation.notes }}
                    onConfirm={async (notes) => {
                      await apiClient.repairOperationsApi.apiV1RepairOperationsRepairOperationIdNotesPut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          repairOperationId: repairOperation.id || "",
                          generalNotesInputDto: { notes },
                        },
                      );
                      onUpdate();
                    }}
                  />
                }
              >
                {repairOperation.notes}
              </InfoTile>
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: {
                  xxs: "1fr",
                  sm: "repeat(2, 1fr)",
                  md: "repeat(4, 1fr)",
                },
                gridTemplateRows: "auto",
                rowGap: 1,
                columnGap: 1,
              }}
            >
              <InfoTile
                label='Mileage'
                iconOf='gauge'
                isEmpty={!repairOperation.mileage}
                endAdornment={
                  <BaseEditMileageModal
                    vehicleId={repairOperation.vehicle?.id}
                    mileage={repairOperation.mileage}
                    trigger={
                      <AppIconButton
                        sx={{ p: "2px", ml: 0.5 }}
                        onClick={() => setIsUpdateMileageModalOpened(true)}
                      >
                        <AppIcon of='edit' sx={{ width: "0.8em", height: "0.8em" }} />
                      </AppIconButton>
                    }
                    triggerParams={{ isDisableDefaultAction: true }}
                    open={isUpdateMileageModalOpened}
                    onClose={() => setIsUpdateMileageModalOpened(false)}
                    onConfirm={async (mileage) => {
                      await apiClient.repairOperationsApi.apiV1RepairOperationsRepairOperationIdMileagePut(
                        {
                          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                          repairOperationId: repairOperation.id || "",
                          updateVehicleMileageDto: {
                            vehicleId: repairOperation.vehicle?.id,
                            mileage: mileage,
                          },
                        },
                      );
                    }}
                    onMileageChanged={() => onUpdate()}
                  />
                }
              >
                {repairOperation.mileage?.mileage} {repairOperation.mileage?.unit}
              </InfoTile>

              <InfoTile
                label='Fuel level'
                iconOf='fuel'
                isEmpty={isNil(repairOperation?.fuelLevel)}
              >
                {repairOperation?.fuelLevel}
              </InfoTile>

              <InfoTile label='Date' iconOf='date' isEmpty={isNil(repairOperation?.date)}>
                <Datetime datetime={repairOperation?.date} />
              </InfoTile>

              <InfoTile
                label='Responsible user'
                iconOf='inspector'
                isEmpty={isNil(repairOperation?.responsibleUser)}
              >
                <GeneralByWhoDisplay
                  who={repairOperation?.responsibleUser}
                  // inlineUserProps={{ withLink: true, withAvatar: true, withPoper: true }}
                />
              </InfoTile>
            </Box>
          </Stack>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.RepairOperation}
          entityId={repairOperation.id}
          entity={repairOperation}
          entityUpdatePermission={AppPermission.RepairOperationManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        {/* Stages */}
        <ViewContentBlock spacing={2}>
          <ViewContentBlockHeader>Stages</ViewContentBlockHeader>
          <GeneralStageHistory
            stageEnumType='RepairOperationStage'
            stageHistory={repairOperation.stageHistory}
            withUpdateButtons={hasPermissions([AppPermission.RepairOperationManage])}
            onUpdateStage={async (newStage) => await handleUpdateStage(newStage)}
          />

          <Alert severity='info' icon={<AppIcon of='tip' />}>
            After the operation is{" "}
            <strong>
              <InlineApiEnumValue
                type='RepairOperationStage'
                value={RepairOperationStage.Completed}
              />
            </strong>
            , the damages will be marked as repaired in the vehicle history.
          </Alert>
        </ViewContentBlock>

        {!ArrayHelper.isEmpty(repairOperation.attachments) && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(repairOperation.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        {/* Items */}
        <ViewContentBlock spacing={2}>
          <ViewContentBlockHeader>Damages</ViewContentBlockHeader>

          <DataTabular
            columns={[
              {
                field: "damage",
                title: "Damage",
                flex: 1,
                renderCell: (item) => (
                  <VehicleDamageLink
                    vehicleId={item.damage?.vehicleId}
                    entity={item.damage}
                    inlineProps={{ withIcon: true }}
                  />
                ),
              },
              {
                field: "notes",
                title: "Notes",
                flex: 1,
                renderCell: (item) =>
                  item.notes ? (
                    <AppTooltip title={item.notes || ""}>
                      <AppTypography ellipsing={{ enabled: true }}>{item.notes}</AppTypography>
                    </AppTooltip>
                  ) : (
                    "-"
                  ),
              },
              {
                field: "attachments",
                title: "Attachments",
                width: 120,
                renderCell: (item) => (
                  <>
                    {item.attachments!.length !== 0 && (
                      <FileListView
                        files={FileItem.createManyFrom(item.attachments)}
                        maxVisibleFiles={2}
                        itemWidth={45}
                      />
                    )}
                  </>
                ),
              },
              {
                field: "cost",
                title: "Cost",
                flex: 1,
                renderCell: (item) => (
                  <CurrencyValue value={item.cost} currency={repairOperation.currency} />
                ),
              },
              {
                field: "discount",
                title: "Discount",
                flex: 1,
                renderCell: (item) => (
                  <GeneralDiscountDisplay
                    discount={item.discount || undefined}
                    currency={repairOperation.currency}
                    detailsPlacement='tooltip'
                  />
                ),
              },
              {
                field: "tax",
                title: "Tax",
                flex: 1,
                renderCell: (item) => (
                  <GeneralTaxDisplay
                    tax={item.tax || undefined}
                    currency={repairOperation.currency}
                  />
                ),
              },
              {
                field: "total",
                title: "Total",
                flex: 1,
                renderCell: (item) => (
                  <CurrencyValue value={item.total} currency={repairOperation.currency} />
                ),
              },
            ]}
            rows={repairOperation.items!}
            getRowId={(item) => item.id!}
            borderVariant='bordered'
            isColumnSelectorEnabled={false}
            rowHeight={65}
            isColumnMenuEnabled={false}
            isPaginationEnabled={false}
            // rowHighlightPropsGetter={(item) =>
            //   (item.id === highlightProps?.itemId && {
            //     isHighlighted: true,
            //     durationMs: 5000,
            //     withAutoScroll: true,
            //   }) ||
            //   {}
            // }
          />
        </ViewContentBlock>

        {/* Summary */}
        <ViewContentBlock alignSelf='flex-end'>
          <GeneralPriceSummaryDisplay
            sx={{ minWidth: { xxs: "100%", md: "300px" } }}
            summary={{
              currency: repairOperation.currency,
              subTotal: repairOperation.subTotal,
              subTotalIncDiscount: repairOperation.subTotalIncDiscount,
              discount: repairOperation.discount,
              tax: repairOperation.tax,
              total: repairOperation.total,
            }}
          />
        </ViewContentBlock>
      </ViewLayout>
    </PageTabContent>
  );
}
